import * as React from 'react';
import { Skeleton } from '@/shared/ui/skeleton';

/**
 * Loading placeholder shaped like the real view — same header block, the same
 * 3-tile row, and stacked section cards — so the page doesn't reflow when data
 * lands (guide rule: skeleton matches the final layout's shape, not a spinner).
 */
export function AnalyticsSkeleton() {
  return (
    <div className="space-y-6" aria-busy="true" aria-live="polite">
      <span className="sr-only">Loading meeting analytics…</span>

      {/* Header */}
      <div className="space-y-4">
        <Skeleton className="h-8 w-28" />
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="space-y-2.5">
            <Skeleton className="h-3 w-24" />
            <Skeleton className="h-8 w-56" />
            <Skeleton className="h-4 w-40" />
          </div>
          <div className="flex gap-2">
            <Skeleton className="h-6 w-16 rounded-full" />
            <Skeleton className="h-6 w-20 rounded-full" />
          </div>
        </div>
      </div>

      {/* Stat tiles */}
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        {[0, 1, 2].map((i) => (
          <div
            key={i}
            className="flex items-start gap-3.5 rounded-[var(--radius-md)] border border-[var(--border)] bg-[var(--surface)] p-4 shadow-[var(--shadow-sm)]"
          >
            <Skeleton className="h-10 w-10 rounded-[var(--radius-sm)]" />
            <div className="flex-1 space-y-2">
              <Skeleton className="h-3 w-20" />
              <Skeleton className="h-5 w-16" />
            </div>
          </div>
        ))}
      </div>

      {/* Section cards */}
      <SectionSkeleton lines={2} />
      <SectionSkeleton lines={3} />
      <SectionSkeleton lines={3} />
    </div>
  );
}

function SectionSkeleton({ lines }: { lines: number }) {
  return (
    <div className="overflow-hidden rounded-[var(--radius-lg)] border border-[var(--border)] bg-[var(--surface)] shadow-[var(--shadow-sm)]">
      <div className="border-b border-[var(--border)] px-5 py-3.5">
        <Skeleton className="h-4 w-32" />
      </div>
      <div className="space-y-3 p-5">
        {Array.from({ length: lines }).map((_, i) => (
          <div key={i} className="flex items-center justify-between gap-4">
            <Skeleton className="h-4 w-24" />
            <Skeleton className="h-4 w-40" />
          </div>
        ))}
      </div>
    </div>
  );
}
